/**
 * Export Service Module
 * Handles Excel/CSV export functionality for safety reports
 */

/* eslint-disable no-console */

import { saveAs } from 'file-saver';
import ExcelJS from 'exceljs';
import { format as formatDate } from 'date-fns';

export class ExportService {
  constructor() {
    this.defaultConfig = {
      companyName: '',
      assessorName: '',
      includeRemediation: true,
      includeCosts: true,
      language: 'de'
    };
    this.config = { ...this.defaultConfig };
  }

  /**
   * Initialize export service
   */
  async init(config = {}) {
    this.config = { ...this.defaultConfig, ...config };
    console.log('📊 Export Service initialized');
  }

  /**
   * Export safety assessment to Excel format
   */
  async exportToExcel(hazards, metadata = {}, projectState = null) {
    try {
      console.log('📈 Generating Excel export...');

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('SUVA Sicherheitsbeurteilung');

      // Set up document metadata
      this.setupWorkbook(workbook, metadata);

      // Create headers
      this.createExcelHeaders(worksheet);

      // Add hazard data
      this.addHazardData(worksheet, hazards);

      // Add summary sheet
      this.addSummarySheet(workbook, hazards, metadata);

      // Add hidden project data sheet if state is provided
      if (projectState) {
        this.addProjectDataSheet(workbook, projectState);
      }

      // Add hidden project data sheet if state is provided
      if (projectState) {
        this.addProjectDataSheet(workbook, projectState);
      }

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = this.generateFileName('xlsx');
      saveAs(new Blob([buffer]), fileName);

      console.log('✅ Excel export completed');
      return { success: true, fileName };
    } catch (error) {
      console.error('❌ Excel export failed:', error);
    }
  }

  /**
   * Import project state from Excel file
   */
  async importProjectFromExcel(file) {
    try {
      console.log('📥 Importing project from Excel...');
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(file);

      const projectSheet = workbook.getWorksheet('_PROJECT_DATA_');
      if (!projectSheet) {
        throw new Error('Keine Projektdaten in dieser Datei gefunden.');
      }

      // Read JSON data from cell A1
      const jsonContent = projectSheet.getCell('A1').value;
      if (!jsonContent) {
        throw new Error('Projektdaten sind leer oder beschädigt.');
      }

      const projectState = JSON.parse(jsonContent);
      console.log('✅ Project import successful:', projectState);
      return { success: true, data: projectState };
    } catch (error) {
      console.error('❌ Project import failed:', error);
      throw new Error(error.message || 'Import fehlgeschlagen');
    }
  }

  /**
   * Export safety assessment to CSV format
   */
  async exportToCSV(hazards, metadata = {}) {
    try {
      console.log('📄 Generating CSV export...');

      // Generate CSV content
      let csvContent = this.generateCSVHeaders();
      csvContent += this.generateCSVData(hazards);
      csvContent += this.generateCSVSummary(hazards);

      // Create file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const fileName = this.generateFileName('csv');
      saveAs(blob, fileName);

      console.log('✅ CSV export completed');
      return { success: true, fileName };
    } catch (error) {
      console.error('❌ CSV export failed:', error);
      throw new Error('CSV-Export fehlgeschlagen');
    }
  }

  /**
   * Setup workbook properties
   */
  setupWorkbook(workbook, metadata) {
    workbook.creator = this.config.assessorName || 'Sicherheitsbeauftragter';
    workbook.lastModifiedBy = workbook.creator;
    workbook.created = new Date();
    workbook.modified = new Date();

    workbook.properties.title = 'SUVA Sicherheitsbeurteilung';
    workbook.properties.subject = `${this.config.companyName || 'Unternehmen'} - Sicherheitsanalyse`;
    workbook.properties.description = 'KI-gestützte Gefährdungsbeurteilung gemäss SUVA-Richtlinien';
    workbook.properties.keywords = 'SUVA, Sicherheit, Gefährdung, Arbeitsplatz, Schweiz';
    workbook.properties.category = 'Sicherheitsbeurteilung';
    workbook.properties.company = this.config.companyName || '';
  }

  /**
   * Create Excel worksheet headers
   */
  createExcelHeaders(worksheet) {
    const headers = [
      'ID',
      'Bereich',
      'SUVA Kategorie',
      'Gefährdung',
      'Beschreibung',
      'Risikostufe',
      'Schweregrad',
      'SUVA Richtlinien',
      'Priorität',
      'Massnahmen',
      'STOP-Stufe',
      'Kostenschätzung',
      'Frist',
      'Status'
    ];

    // Add header row
    worksheet.addRow(headers);

    // Style header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: this.config.primaryColor || 'FFd41e2a' }
    };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

    // Set column widths
    worksheet.columns = [
      { width: 10 }, // ID
      { width: 20 }, // Bereich
      { width: 25 }, // SUVA Kategorie
      { width: 20 }, // Gefährdung
      { width: 40 }, // Beschreibung
      { width: 12 }, // Risikostufe
      { width: 12 }, // Schwergrad
      { width: 30 }, // SUVA Richtlinien
      { width: 12 }, // Priorität
      { width: 35 }, // Massnahmen
      { width: 10 }, // STOP-Stufe
      { width: 15 }, // Kostenschätzung
      { width: 12 }, // Frist
      { width: 15 } // Status
    ];
  }

  /**
   * Add hazard data to Excel worksheet
   */
  addHazardData(worksheet, hazards) {
    hazards.hazards?.forEach((hazard, index) => {
      const rowData = [
        hazard.id || `H${String(index + 1).padStart(3, '0')}`,
        hazard.area || '',
        hazard.category || '',
        hazard.subcategory || '',
        hazard.description || '',
        hazard.riskLevel || '',
        this.calculateSeverity(hazard.riskLevel, hazard.priority),
        (hazard.suvaGuidelines || []).join('; '),
        hazard.remediation?.priority || '',
        (hazard.remediation?.measures || []).join('; '),
        hazard.remediation?.stopLevel || '',
        this.config.includeCosts ? (hazard.remediation?.costEstimate || '') : '',
        this.calculateDueDate(hazard.remediation?.priority),
        'Offen'
      ];

      worksheet.addRow(rowData);
    });

    // Add conditional formatting
    this.addConditionalFormatting(worksheet);
  }

  /**
   * Add summary sheet to workbook
   */
  addSummarySheet(workbook, hazards, metadata) {
    const summarySheet = workbook.addWorksheet('Zusammenfassung');

    // Company information
    summarySheet.addRow(['Firmendaten']);
    summarySheet.addRow(['Firma:', this.config.companyName || '']);
    summarySheet.addRow(['Datum:', formatDate(new Date(), 'dd.MM.yyyy')]);
    summarySheet.addRow(['Bearbeiter:', this.config.assessorName || '']);
    summarySheet.addRow(['']);

    // Hazard statistics
    const stats = this.calculateHazardStats(hazards);
    summarySheet.addRow(['Statistiken']);
    summarySheet.addRow(['Total Gefährdungen:', stats.total]);
    summarySheet.addRow(['Hohe Risiken:', stats.highRisk]);
    summarySheet.addRow(['Mittlere Risiken:', stats.mediumRisk]);
    summarySheet.addRow(['Geringe Risiken:', stats.lowRisk]);
    summarySheet.addRow(['']);

    // Cost summary (if enabled)
    if (this.config.includeCosts) {
      summarySheet.addRow(['Kostenschätzung']);
      summarySheet.addRow(['Gesamtkosten:', `CHF ${stats.totalCost}`]);
      summarySheet.addRow(['']);
    }

    // Style summary
    this.formatSummarySheet(summarySheet);
  }

  /**
   * Generate CSV headers
   */
  generateCSVHeaders() {
    const headers = [
      'ID',
      'Bereich',
      'SUVA_Kategorie',
      'Gefährdung',
      'Beschreibung',
      'Risikostufe',
      'SUVA_Richtlinien',
      'Priorität',
      'Massnahmen',
      'Kostenschätzung'
    ];

    return `${headers.join(';')}\n`;
  }

  /**
   * Generate CSV data rows
   */
  generateCSVData(hazards) {
    let csvData = '';

    hazards.hazards?.forEach((hazard, index) => {
      const row = [
        `H${String(index + 1).padStart(3, '0')}`,
        hazard.area || '',
        hazard.category || '',
        hazard.subcategory || '',
        `"${(hazard.description || '').replace(/"/g, '""')}"`,
        hazard.riskLevel || '',
        `"${(hazard.suvaGuidelines || []).join('; ').replace(/"/g, '""')}"`,
        hazard.remediation?.priority || '',
        `"${(hazard.remediation?.measures || []).join('; ').replace(/"/g, '""')}"`,
        this.config.includeCosts ? (hazard.remediation?.costEstimate || '') : ''
      ];

      csvData += `${row.join(';')}\n`;
    });

    return csvData;
  }

  /**
   * Generate CSV summary
   */
  generateCSVSummary(hazards) {
    const stats = this.calculateHazardStats(hazards);

    let summary = '\n# ZUSAMMENFASSUNG\n';
    summary += `Firma;${this.config.companyName}\n`;
    summary += `Datum;${formatDate(new Date(), 'dd.MM.yyyy')}\n`;
    summary += `Total Gefährdungen;${stats.total}\n`;
    summary += `Hohe Risiken;${stats.highRisk}\n`;
    summary += `Mittlere Risiken;${stats.mediumRisk}\n`;
    summary += `Geringe Risiken;${stats.lowRisk}\n`;

    if (this.config.includeCosts) {
      summary += `Gesamtkosten;CHF ${stats.totalCost}\n`;
    }

    return summary;
  }

  /**
   * Calculate hazard statistics
   */
  calculateHazardStats(hazards) {
    const stats = {
      total: 0,
      highRisk: 0,
      mediumRisk: 0,
      lowRisk: 0,
      totalCost: 0
    };

    hazards.hazards?.forEach(hazard => {
      if (hazard.riskLevel === 'hoch') stats.highRisk++;
      else if (hazard.riskLevel === 'mittel') stats.mediumRisk++;
      else stats.lowRisk++;

      stats.total++;

      if (this.config.includeCosts && hazard.remediation?.costEstimate) {
        stats.totalCost += this.parseCostEstimate(hazard.remediation.costEstimate);
      }
    });

    return stats;
  }

  /**
   * Parse cost estimate to numeric value
   */
  parseCostEstimate(estimate) {
    if (typeof estimate === 'number') return estimate;
    if (typeof estimate === 'string') {
      const match = estimate.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    }
    return 0;
  }

  /**
   * Calculate severity based on risk level and priority
   */
  calculateSeverity(riskLevel, priority) {
    if (riskLevel === 'hoch' && priority === 'sofort') return 'Kritisch';
    if (riskLevel === 'hoch') return 'Hoch';
    if (riskLevel === 'mittel') return 'Mittel';
    return 'Gering';
  }

  /**
   * Calculate due date based on priority
   */
  calculateDueDate(priority) {
    const today = new Date();

    switch (priority) {
      case 'sofort':
        return formatDate(today, 'dd.MM.yyyy');
      case 'fristig':
        return formatDate(new Date(today.setDate(today.getDate() + 30)), 'dd.MM.yyyy');
      case 'mittel':
        return formatDate(new Date(today.setDate(today.getDate() + 90)), 'dd.MM.yyyy');
      default:
        return formatDate(new Date(today.setDate(today.getDate() + 180)), 'dd.MM.yyyy');
    }
  }

  /**
   * Generate filename with timestamp
   */
  generateFileName(extension) {
    const company = this.config.companyName || 'Unbenannt';
    const timestamp = formatDate(new Date(), 'yyyyMMdd_HHmmss');
    const safeName = company.replace(/[^a-zA-Z0-9]/g, '_');

    return `SUVA_Sicherheitsanalyse_${safeName}_${timestamp}.${extension}`;
  }

  /**
   * Add conditional formatting to Excel worksheet
   */
  addConditionalFormatting(worksheet) {
    // Add risk level color coding
    worksheet.addConditionalFormatting({
      ref: 'F2:F1000', // Risk level column
      rules: [
        {
          type: 'expression',
          formulae: ['F2="hoch"'],
          style: { fill: { bgColor: { argb: 'FFffcdd2' } } }
        },
        {
          type: 'expression',
          formulae: ['F2="mittel"'],
          style: { fill: { bgColor: { argb: 'FFfff3cd' } } }
        },
        {
          type: 'expression',
          formulae: ['F2="gering"'],
          style: { fill: { bgColor: { argb: 'FFd4edda' } } }
        }
      ]
    });
  }

  /**
   * Format summary sheet
   */
  formatSummarySheet(sheet) {
    // Make headers bold
    const boldRow = row => {
      row.font = { bold: true };
      row.eachCell((cell) => {
        cell.font = { bold: true };
      });
    };

    boldRow(sheet.getRow(1)); // Firmendaten
    boldRow(sheet.getRow(5)); // Statistiken
    boldRow(sheet.getRow(10)); // Kostenschätzung (if exists)

    // Auto-fit columns
    sheet.columns.forEach(column => {
      column.width = 20;
    });
  }

  /**
   * Add hidden project data sheet
   */
  addProjectDataSheet(workbook, projectState) {
    try {
      const sheet = workbook.addWorksheet('_PROJECT_DATA_');

      // Serialize project state
      const jsonState = JSON.stringify(projectState);

      // Store in cell A1
      sheet.getCell('A1').value = jsonState;

      // Hide the sheet
      sheet.state = 'veryHidden';

      console.log('💾 Added hidden project data to Excel');
    } catch (error) {
      console.warn('⚠️ Failed to add project data to Excel:', error);
    }
  }

  /**
   * Update export configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return { ...this.config };
  }
}
